import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  Chip,
  Skeleton,
  Link
} from '@mui/material';
import {
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { calcReputationDelta } from '../../utils/reputationUtils';
import ReputationPill from '../common/ReputationPill';
import { FancySkeleton } from '../common/FancySkeleton';
import { useCreateWithTrialCheck } from '../../hooks/useCreateWithTrialCheck';
import TrialStatusModal from '../TrialStatusModal';

interface RecentIdea {
  id: string;
  topic: string;
  brand: string;
  platform: string;
  createdAt: string;
  draftCount: number;
  reputationDelta?: number;
  reputation?: {
    uiScore: number;
    level: string;
  } | null;
}

interface RecentIdeasProps {
  ideas?: RecentIdea[];
  loading?: boolean;
}

export const RecentIdeas: React.FC<RecentIdeasProps> = ({
  ideas = [],
  loading = false
}) => {
  const navigate = useNavigate();
  
  const {
    handleCreateAction,
    isTrialStatusModalOpen,
    closeTrialStatusModal,
    isAdmin
  } = useCreateWithTrialCheck();

  if (loading) {
    return (
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Ideas
          </Typography>
          <List>
            {[1, 2, 3].map((i) => (
              <ListItem key={i} sx={{ px: 0 }}>
                <ListItemText
                  primary={<Skeleton variant="text" width="80%" />}
                  secondary={<Skeleton variant="text" width="60%" />}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Recent Ideas
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/ideas')}
            underline="hover"
            sx={{ cursor: 'pointer' }}
          >
            View All
          </Link>
        </Box>

        {ideas.length === 0 ? (
          <Box textAlign="center" py={3}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              No ideas generated yet
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<LightbulbIcon />}
              onClick={() => handleCreateAction(() => navigate('/ideas/create'))}
              size="small"
              sx={{ mt: 2, textTransform: 'none' }}
            >
              Generate Your First Idea
            </Button>
          </Box>
        ) : (
          <>
            <List sx={{ py: 0 }}>
              {ideas.map((idea) => (
                <ListItem
                  key={idea.id}
                  sx={{
                    px: 0,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 1
                  }}
                  onClick={() => navigate(`/ideas/${idea.id}/results`)}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {idea.topic}
                      </Typography>
                      {(() => {
                        // If reputation present, render pill; otherwise show skeleton for in-progress
                        if (idea.reputation && typeof idea.reputation.uiScore === 'number') {
                          return <ReputationPill uiScore={idea.reputation.uiScore} level={idea.reputation.level} />;
                        }
                        return <FancySkeleton height={18} width={110} radius={10} />;
                      })()}
                    </Box>
                    <Box display="flex" gap={1} alignItems="center" mt={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {idea.brand}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {idea.platform}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(new Date(idea.createdAt), { addSuffix: true })}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<LightbulbIcon />}
              onClick={() => handleCreateAction(() => navigate('/ideas/create'))}
              fullWidth
              size="small"
              sx={{ mt: 2, textTransform: 'none' }}
            >
              Generate New Idea
            </Button>
          </>
        )}
      </CardContent>
    </Card>
    
    <TrialStatusModal 
      isOpen={isTrialStatusModalOpen} 
      onClose={closeTrialStatusModal} 
      isAdmin={isAdmin}
      status="expired"
    />
    </>
  );
};
